import type { AggregatedProjectData, AICheckRunResult } from "@/types/aicheck";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    const text = asString(value);
    if (text) return text;
  }
  return null;
}

export function deriveProjectTitle(
  project: unknown,
  projectId: number,
  fallbackTitle?: string | null,
): string {
  const record = asRecord(project);
  const directTitle = firstString(
    record.name_board_name,
    record.title,
    record.name,
    record.project_name,
    record.tour_name,
    record.itinerary_name,
  );
  if (directTitle) return directTitle;

  const agencyName = asString(asRecord(record.agency).name);
  const customerNames = asArray(record.customer_projects)
    .map((item) => asString(asRecord(asRecord(item).customer).name))
    .filter((item): item is string => item != null);
  const travelersCount =
    typeof record.travelers_count === "number" && record.travelers_count > 0
      ? `${record.travelers_count}PAX`
      : null;
  const descriptiveTitle = [
    agencyName,
    customerNames.slice(0, 2).join(", "),
    travelersCount,
  ]
    .filter((item): item is string => Boolean(item))
    .join(" ");
  if (descriptiveTitle) return descriptiveTitle;

  const tourCode = asString(record.tour_code);
  if (tourCode) return `Project ${tourCode}`;

  const travelStart = asString(record.travel_start_date);
  const travelEnd = asString(record.travel_end_date);
  const dateRange =
    travelStart && travelEnd
      ? `${travelStart} - ${travelEnd}`
      : (travelStart ?? travelEnd);
  if (dateRange) return dateRange;

  const fallback = asString(fallbackTitle);
  return fallback ?? `Project ${projectId}`;
}

export function projectTitleFromProjectData(
  projectData: AggregatedProjectData | null | undefined,
  projectId: number,
  fallbackTitle?: string | null,
): string {
  return deriveProjectTitle(projectData?.project, projectId, fallbackTitle);
}

export function projectTitleFromRunResult(
  result: Pick<
    AICheckRunResult,
    "projectId" | "projectData" | "projectDataSource"
  >,
): string {
  return projectTitleFromProjectData(
    result.projectData,
    result.projectId,
    result.projectDataSource?.label,
  );
}
